import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type { UIPaymentGroups, UIPayoutGroups } from '../formatters';
import type { CarouselState } from '../models';

export const initialState: CarouselState = {
  isInitialLoad: false,
  isCarouselViewChanged: false,
  methodList: [],
  isShowAllMethods: false,
  isCarouselReadyForForm: false,
  isCarouselFormLoadedOnce: false,
};

const carouselSlice = createSlice({
  name: 'carousel',
  initialState,
  reducers: {
    setCarouselMethods: (state, action: PayloadAction<(UIPaymentGroups | UIPayoutGroups)[]>) => ({
      ...state,
      methodList: action.payload,
    }),
    setInitialCarouselLoad: (state, action: PayloadAction<boolean>) => ({
      ...state,
      isInitialLoad: action.payload,
    }),
    setCarouselViewOff: (state) => ({
      ...state,
      isCarouselViewChanged: true,
    }),
    setShowAllMethods: (state, action: PayloadAction<boolean>) => ({
      ...state,
      isShowAllMethods: action.payload,
    }),
    setCarouselReadyForForm: (state, action: PayloadAction<boolean>) => ({
      ...state,
      isCarouselReadyForForm: action.payload,
    }),
    setCarouselFormLoadedOnce: (state) => ({
      ...state,
      isCarouselFormLoadedOnce: true,
    }),
    resetCarouselFormLoadedOnce: (state) => ({
      ...state,
      isCarouselFormLoadedOnce: false,
    }),
  },
});

export const carouselMethodListSelector = ({
  carousel,
}: {
  carousel: CarouselState;
}): (UIPayoutGroups | UIPaymentGroups)[] => carousel.methodList || [];

export const isCarouselViewChangedSelector = ({ carousel }: { carousel: CarouselState }): boolean =>
  carousel.isCarouselViewChanged;

export const isCarouselInitLoadSelector = ({ carousel }: { carousel: CarouselState }): boolean =>
  carousel.isInitialLoad;

export const isCarouselViewSelector = ({ carousel }: { carousel: CarouselState }): boolean =>
  carousel.isInitialLoad &&
  !carousel.isCarouselViewChanged &&
  carousel.methodList.length > 1 &&
  !carousel.isShowAllMethods;

export const isCarouselReadySelector = ({ carousel }: { carousel: CarouselState }): boolean =>
  carousel.isShowAllMethods && carousel.isInitialLoad && carousel.methodList.length > 0;

export const {
  setCarouselMethods,
  setCarouselViewOff,
  setInitialCarouselLoad,
  setShowAllMethods,
  setCarouselReadyForForm,
  setCarouselFormLoadedOnce,
  resetCarouselFormLoadedOnce,
} = carouselSlice.actions;

export const isCarouselReadyForFormSelector = ({ carousel }: { carousel: CarouselState }): boolean =>
  carousel.isCarouselReadyForForm;

export const isCarouselFormLoadedOnceSelector = ({ carousel }: { carousel: CarouselState }): boolean =>
  carousel.isCarouselFormLoadedOnce;

export default carouselSlice.reducer;
